import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import {
    OrderSyncProgress,
    OrderSyncProgressStatus,
    supplierIncrementally,
    supplierSyncByOriginalIds,
} from "@/services/api/orderSync";
import to from "@/utils/to";
import { message, Modal, Spin } from "antd";
import React, { useEffect, useRef, useState } from "react";
import OrderSyncType from "./OrderSyncType";
import StopButton from "./StopButton";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { OverwriteInterface } from "@/utils/utilityTypes";
import { useGlobalStore } from "@/store/hooks";
import PartnershipDTO from "@/dto/partnership/PartnershipDTO";

interface BaseSyncOrderProcessModalProps extends InjectBaseModalProps {
    orderSyncType: OrderSyncType;

    supplierShopId: number;

    partnership: PartnershipDTO;

    tab: OrderTab;
}

interface IncrementSyncOrderProcessModalProps
    extends OverwriteInterface<BaseSyncOrderProcessModalProps, { orderSyncType: OrderSyncType.INCREMENT }> {}

interface OriginalIdsSyncOrderProcessModalProps
    extends OverwriteInterface<BaseSyncOrderProcessModalProps, { orderSyncType: OrderSyncType.ORIGINAL_IDS }> {
    originalIds: string;
}

const SyncOrderProcessModal: React.FC<
    IncrementSyncOrderProcessModalProps | OriginalIdsSyncOrderProcessModalProps
> = function (props) {
    const { orderSyncType, supplierShopId, partnership, open, onCancel, tab } = props;

    const originalIds = props.orderSyncType === OrderSyncType.ORIGINAL_IDS ? props.originalIds : undefined;

    const [orderSyncProgress, updateOrderSyncProgress] = useState<OrderSyncProgress>(undefined);

    const globalStore = useGlobalStore();
    const orderManagementStore = useOrderManagementStore();

    const timer = useRef<NodeJS.Timeout>();

    async function incrementSyncOrder() {
        const shopId = partnership.shopId;

        const [error, res] = await to(supplierIncrementally({ shopId, supplierShopId }));

        if (error) {
            onCancel();

            return;
        }

        updateOrderSyncProgress(res);
    }

    async function syncByOriginalIds() {
        const shopId = partnership.shopId;

        const [error] = await to(supplierSyncByOriginalIds({ shopId, supplierShopId, originalIds }));

        if (!error) {
            orderManagementStore.fetchOrders(supplierShopId, tab);

            message.success("同步订单成功");
        }

        onCancel();
    }

    function handleStop() {
        onCancel();

        if (timer.current) {
            clearTimeout(timer.current);
        }
    }

    useEffect(() => {
        if (orderSyncType === OrderSyncType.INCREMENT) {
            incrementSyncOrder();
        } else if (orderSyncType === OrderSyncType.ORIGINAL_IDS) {
            syncByOriginalIds();
        }
    }, [orderSyncType]);

    useEffect(() => {
        if (orderSyncProgress) {
            const { status, lastSyncOrderTime } = orderSyncProgress;

            if (status === OrderSyncProgressStatus.COMPLETED) {
                onCancel();

                orderManagementStore.fetchOrders(supplierShopId, tab);

                globalStore.updateLastSyncOrderTime(supplierShopId, lastSyncOrderTime);

                orderManagementStore?.getOrderListCount(supplierShopId, tab);

                message.success("同步订单成功");
            }

            if (status === OrderSyncProgressStatus.RUNNING) {
                // 隔1秒轮询
                timer.current = setTimeout(() => {
                    incrementSyncOrder();
                }, 1000);
            }
        }
    }, [orderSyncProgress]);

    let loadingMessage = `正在同步订单，代理商:${partnership.alias || partnership.name}`;

    if (orderSyncProgress?.lastSyncOrderTime) {
        loadingMessage += `，进度：${orderSyncProgress.lastSyncOrderTime}`;
    }

    return (
        <Modal width={600} open={open} closable={false} footer={null} mask={{ closable: false }} keyboard={false}>
            <div>
                <Spin className="mr10" />
                {loadingMessage}
                {orderSyncType === OrderSyncType.INCREMENT && (
                    <StopButton supplierShopId={supplierShopId} onStop={handleStop} />
                )}
            </div>

            {orderSyncProgress?.message ? (
                <div
                    style={{
                        marginLeft: 30,
                    }}
                >
                    {orderSyncProgress?.message}
                </div>
            ) : null}
        </Modal>
    );
};

export default React.memo(SyncOrderProcessModal);
